import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import Button from "./Button";
import { Field, TextArea, SelectField } from "./Field";
import NorthStar from "./NorthStar";
import client, { formatApiError } from "../lib/api";

const INITIAL = {
  full_name: "",
  email: "",
  phone: "",
  primary_social: "",
  platform_links: "",
  audience_size: "",
  content_category: "",
  management_status: "",
  why_interested: "",
  anything_else: "",
};

export default function ApplicationForm() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await client.post("/applications", form);
      setSent(true);
      setForm(INITIAL);
      toast.success("Application received. We'll be in touch if there's a fit.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="apply" className="border-t border-charcoal py-16 sm:py-32" data-testid="application-section">
      <div className="mx-auto max-w-[1600px] px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="label mb-8">Join The Roster</div>
            <h2 className="font-sans font-semibold uppercase text-warm text-4xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-[-0.02em]">
              Ready to go pro?
            </h2>
            <p className="mt-8 text-silver text-lg leading-relaxed font-light max-w-md">
              We work with ambitious athletes, creators and entertainers serious about building
              careers that move culture, on and off the feed.
            </p>
            <div className="mt-10 flex items-start gap-3 max-w-md">
              <NorthStar className="w-3 h-3 text-silver mt-1 shrink-0" glow={false} />
              <p className="text-silver/70 text-sm font-light leading-relaxed">
                Due to submission volume, our team may only respond to applicants who are a
                potential fit.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          {sent ? (
            <Reveal>
              <div className="border border-charcoal p-10 sm:p-14" data-testid="application-success">
                <NorthStar className="w-6 h-6 text-warm mb-6" />
                <h3 className="font-serif text-4xl text-warm">Application received.</h3>
                <p className="mt-4 text-silver font-light max-w-md">
                  Thank you for your interest in NSTR Talent. If your profile aligns with our
                  roster, a member of our team will reach out.
                </p>
                <button
                  className="mt-8 label link-underline !text-warm"
                  onClick={() => setSent(false)}
                  data-testid="application-reset"
                >
                  Submit another
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10" data-testid="application-form">
                <Field label="Full name" name="full_name" value={form.full_name} onChange={onChange} required testId="app-full-name" />
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testId="app-email" />
                <Field label="Phone number" name="phone" value={form.phone} onChange={onChange} testId="app-phone" />
                <Field label="Primary social handle" name="primary_social" value={form.primary_social} onChange={onChange} placeholder="@" testId="app-social" />
                <div className="sm:col-span-2">
                  <Field label="Platform links" name="platform_links" value={form.platform_links} onChange={onChange} placeholder="Instagram, TikTok, YouTube…" testId="app-links" />
                </div>
                <Field label="Audience size" name="audience_size" value={form.audience_size} onChange={onChange} testId="app-audience" />
                <SelectField
                  label="Content category"
                  name="content_category"
                  value={form.content_category}
                  onChange={onChange}
                  testId="app-category"
                  options={["Lifestyle", "Fashion", "Beauty", "Music", "Entertainment", "Sports", "Gaming", "Food", "Travel", "Other"]}
                />
                <div className="sm:col-span-2">
                  <SelectField
                    label="Current management status"
                    name="management_status"
                    value={form.management_status}
                    onChange={onChange}
                    testId="app-management"
                    options={["Unrepresented", "Currently represented", "Between management", "Self-managed"]}
                  />
                </div>
                <div className="sm:col-span-2">
                  <TextArea label="Why are you interested in NSTR Talent?" name="why_interested" value={form.why_interested} onChange={onChange} rows={3} testId="app-why" />
                </div>
                <div className="sm:col-span-2">
                  <TextArea label="Anything else we should know?" name="anything_else" value={form.anything_else} onChange={onChange} rows={3} testId="app-else" />
                </div>
                <div className="sm:col-span-2 mt-2">
                  <Button type="submit" variant="primary" disabled={loading} data-testid="app-submit">
                    {loading ? "Submitting…" : "Submit Application"}
                  </Button>
                </div>
              </form>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
